import { Role, version } from "@pentacode/core";
import { PUBLIC_PTC_HELPSCOUT_BEACON_ID } from "../env";
import { app, router } from "../init";

type BeaconMethod =
    | "init"
    | "destroy"
    | "open"
    | "close"
    | "toggle"
    | "config"
    | "identify"
    | "session-data"
    | "prefill"
    | "reset"
    | "search"
    | "article"
    | "navigate"
    | "suggest"
    | "event"
    | "on"
    | "off"
    | "once"
    | "show-message"
    | "logout";

interface BeaconFn {
    (method: BeaconMethod, options?: unknown, data?: unknown): unknown;
    readyQueue?: Array<{ method: string; options: unknown; data: unknown }>;
}

declare global {
    interface Window {
        Beacon?: BeaconFn;
    }
}

const BEACON_SRC = "https://beacon-v2.helpscout.net";

function installLoader(): BeaconFn {
    if (window.Beacon) return window.Beacon;

    const readyQueue: NonNullable<BeaconFn["readyQueue"]> = [];
    const stub: BeaconFn = Object.assign(
        (method: BeaconMethod, options?: unknown, data?: unknown): void => {
            readyQueue.push({ method, options, data });
        },
        { readyQueue }
    );
    window.Beacon = stub;

    const inject = () => {
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.src = BEACON_SRC;
        const target = document.head ?? document.getElementsByTagName("script")[0]?.parentNode;
        target?.appendChild(script);
    };

    if (document.readyState === "complete") {
        inject();
    } else {
        window.addEventListener("load", inject);
    }

    return stub;
}

function beacon(method: BeaconMethod, options?: unknown, data?: unknown): unknown {
    return window.Beacon?.(method, options, data);
}

export function contactSupport(subject = "") {
    beacon("session-data", {
        "App Version": version,
        "Company ID": app.company?.id,
        "Session ID": (app.state.session && app.state.session.id) || "",
    });
    beacon("prefill", {
        subject,
        fields: [{ id: 33862, value: app.company?.id }],
    });
    beacon("navigate", "/ask/message/");
    beacon("open");
    beacon("once", "close", () => {
        beacon("prefill", { subject: "" });
        beacon("navigate", "/");
    });
}

let initialized = false;

// The Helpscout beacon is a heavy 3rd-party widget not needed for first paint.
// Init defers the actual wiring until the browser is idle, bounded so a
// continuously busy page can't defer it indefinitely (3s worst case).
// Standalone-only (see initApp), and a standalone page never calls disposeApp,
// so the subscriptions below live for the lifetime of the page and need no
// teardown. Initializing the beacon from a disposable (embedded) mount would
// have to cancel the pending idle callback as well.
export function initHelpscout() {
    if (initialized) {
        return;
    }
    initialized = true;

    if ("requestIdleCallback" in window) {
        window.requestIdleCallback(() => wireHelpscout(), { timeout: 3000 });
    } else {
        setTimeout(wireHelpscout, 3000);
    }
}

function wireHelpscout() {
    if (PUBLIC_PTC_HELPSCOUT_BEACON_ID) {
        installLoader()("init", PUBLIC_PTC_HELPSCOUT_BEACON_ID);
    }

    beacon("config", {
        display: { position: "left" },
        messaging: {
            contactForm: {
                customFieldsEnabled: true,
            },
        },
        showPrefilledCustomFields: false,
    });

    const identify = () => {
        const role = app.profile && app.profile.role;
        const roleTitle =
            role === null
                ? ""
                : role === Role.Admin
                  ? "Admin"
                  : role === Role.Owner
                    ? "Besitzer"
                    : role === Role.Manager
                      ? "Manager"
                      : "Mitarbeiter";

        const params: Record<string, unknown> = {
            email: (app.account && app.account.email) || "",
            name: (app.account && app.account.name) || "",
            jobTitle: roleTitle,
            accountId: (app.account && app.account.id) || "",
            sessionId: (app.state.session && app.state.session.id) || "",
            company: (app.company && app.company.name) || "",
            companyId: (app.company && app.company.id) || "",
            companyPhone: (app.company && app.company.phone) || "",
            employeePhone: (app.profile && app.profile.phone) || "",
            appVersion: version,
            role: roleTitle,
        };

        if (app.profile && app.profile.avatar) {
            params.avatar = app.profile.avatar;
        }

        beacon("identify", params);
    };
    app.subscribe(identify);

    const onRouteChanged = () => {
        setTimeout(() => {
            beacon("event", {
                type: "page-viewed",
                url: window.location.href,
                title: document.title,
            });
            beacon("suggest");
        }, 100);
    };
    router.addEventListener("route-changed", onRouteChanged);
}
